// ===========================================
// PROOF OF DELIVERY — one small affordance on an order row
// ===========================================
// A delivery photo or signed slip is just an attachment with kind='pod' (the
// same order-attachment pipe that carries POs and BOLs — not a new system).
// No proof yet → a camera button to add one; proof there → a green check that
// opens it.

import { useRef, useState } from 'react';
import { Camera, FileCheck2 } from 'lucide-react';
import { wholesaleApi } from '../../services/api';

type Attachment = { id: string; name: string; kind?: string; mimeType?: string };

export default function ProofOfDelivery({ orderId, attachments, onSaved, onError }: {
  orderId: string;
  attachments: Attachment[];
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const pod = (attachments || []).find((a) => a?.kind === 'pod');

  const upload = async (file: File) => {
    setBusy(true);
    try {
      // 'pod' is accepted server-side alongside po|bol|other; the client
      // helper's type predates it, hence the cast.
      await wholesaleApi.uploadOrderAttachment(orderId, file, 'pod' as any);
      onSaved('Proof of delivery attached.');
    } catch {
      onError("The proof of delivery couldn't be uploaded — try again.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Attachments live in private blob storage, so viewing goes through the
  // API (which holds the read token) rather than a direct URL.
  const view = async () => {
    if (!pod) return;
    try {
      const res = await wholesaleApi.downloadOrderAttachment(orderId, pod.id);
      const url = URL.createObjectURL(res.data as Blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      onError("The proof of delivery couldn't be opened just now.");
    }
  };

  if (pod) {
    return (
      <button onClick={(e) => { e.stopPropagation(); void view(); }}
        className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"
        title={`Proof of delivery — ${pod.name}`}>
        <FileCheck2 className="w-3.5 h-3.5" />
      </button>
    );
  }
  return (
    <>
      <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
      <button onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }} disabled={busy}
        className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded disabled:opacity-50"
        title="Attach proof of delivery (photo or PDF)">
        <Camera className="w-3.5 h-3.5" />
      </button>
    </>
  );
}
